import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import Hero from "@/components/site/Hero";
import NowPlaying from "@/components/site/NowPlaying";
import { Reveal } from "@/components/site/Reveal";
import YouTubeSection from "@/components/site/YouTubeSection";
import InstagramCTA from "@/components/site/InstagramCTA";
import CamUp from "@/components/site/CamUp";
import PageLoader from "@/components/site/PageLoader";

export default function Home() {
  const c = useContent();
  if (!c) return <PageLoader />;

  const AWARD_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/35s2ywb7_image.png";
  const cards = [
    { label: "Origin Story", to: "/biography", overline: "The Origin Story", image: c.bio?.[2]?.image, span: "md:col-span-7", h: "h-[340px] md:h-[440px]" },
    { label: "The Soundprint", to: "/discography", overline: "The Catalog", image: "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/0cz3ze3w_ChatGPT%20Image%20Aug%203%2C%202026%2C%2003_25_56%20PM.png", pos: "center 25%", span: "md:col-span-5", h: "h-[340px] md:h-[440px]" },
    { label: "Bragging Rights", to: "/awards", overline: "Honors", image: AWARD_IMG, pos: "center 22%", span: "md:col-span-4", h: "h-[300px]" },
    { label: "Iconography", to: "/gallery", overline: "Iconography", image: c.gallery?.[0]?.image, span: "md:col-span-4", h: "h-[300px]" },
    { label: "Press", to: "/press", overline: "Features", image: "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/d1cplksz_1.png", pos: "center top", span: "md:col-span-4", h: "h-[300px]" },
    { label: "Stage Zero", to: "/stage-zero", overline: "The AI King", image: "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/6jbk2asb_001.jpg", pos: "center 20%", span: "md:col-span-4", h: "h-[300px]" },
    { label: "Events", to: "/the-weekend", overline: "Timbaland Way", image: c.event?.image, span: "md:col-span-7", h: "h-[300px] md:h-[380px]" },
    { label: "MasterClass", to: "/masterclass", overline: "Learn The Craft", image: "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/16z3buas_image.png", span: "md:col-span-5", h: "h-[300px] md:h-[380px]" },
  ];

  return (
    <div className="bg-obsidian" data-testid="home-page">
      <Hero hero={{ ...c.hero, cta_target: "#now-playing" }} />

      <NowPlaying />

      <section id="intro" className="relative pt-24 md:pt-40 pb-12 md:pb-40 border-b border-white/10" data-testid="home-intro">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <p className="overline text-gold mb-6">Welcome to the HQ</p>
              <h2 className="headline text-4xl md:text-6xl text-white leading-[0.95]">
                The sound
                <br />
                that <span className="font-editorial italic normal-case tracking-normal text-gold">changed</span>
                <br />
                everything.
              </h2>
            </Reveal>
          </div>

          <div className="md:col-span-8 space-y-7">
            <Reveal delay={0.1}>
              <p className="font-body text-lg md:text-xl leading-relaxed text-white/70">
                Let's be real: most of the internet makes you play scavenger hunt just to piece together the full
                Timbaland story. One tab for Wikipedia, another for random fan blogs, a third for half-baked
                discographies, multiple social media platforms, and don't forget the 27th Google search just to find
                that one track you know he produced. <span className="text-white">Exhausting, right?</span>
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-body text-lg md:text-xl leading-relaxed text-white/70">
                This is different. This is the one-stop shop derived from Tim's very first website that dominated the
                90s. The headquarters. The "everything you've been looking for" spot. Here you'll find the beats, the
                biography, the discography, the never-before-seen photo archives, the memories, the milestones, and
                yes… <span className="text-gold">every single receipt.</span>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-body text-lg md:text-xl leading-relaxed text-white/70">
                Think of it as the Timbaland Library of Congress… but with a VIP pass. Every era, every hit, every
                game-changing collaboration. All curated under one roof. Whether you're a deep-diving music nerd, a
                casual scroller, or just here to remind yourself who put the "super" in "super producer," you're exactly
                where you need to be.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="archive" className="relative pt-12 md:pt-36 pb-24 md:pb-36" data-testid="archive-section">
        {/* faint DJ image so it isn't plain black */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
          <img src={c.hero?.background_image} alt="" className="w-full h-full object-cover opacity-[0.05] grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-14 md:mb-20 max-w-2xl"
          >
            <p className="overline text-gold mb-6">Enter The Archive</p>
            <h2 className="headline text-5xl md:text-7xl text-white">
              The full <span className="font-editorial italic normal-case tracking-normal text-gold">story.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {cards.map((card, i) => (
              <motion.div
                key={card.to}
                className={card.span}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={card.to}
                  data-testid={`home-card-${card.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`group relative block w-full ${card.h} overflow-hidden`}
                >
                  {card.image && (
                    <img src={card.image} alt={card.label} style={{ objectPosition: card.pos || "center" }} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms] group-hover:scale-105" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20 group-hover:from-black/80 transition-colors" />
                  <div className="absolute inset-0 p-7 md:p-9 flex flex-col justify-between">
                    <span className="overline text-gold">{card.overline}</span>
                    <div className="flex items-end justify-between">
                      <h3 className="headline text-3xl md:text-5xl text-white">{card.label}</h3>
                      <span className="w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white group-hover:bg-gold group-hover:text-black group-hover:border-gold transition-colors">
                        <ArrowUpRight size={18} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CamUp />

      <InstagramCTA url={c.media?.instagram_url} />

      <YouTubeSection videos={c.media?.videos || []} channelUrl={c.media?.channel_url} instagramEmbeds={c.media?.instagram_embeds} instagramReel={c.media?.instagram_reel} />

      {/* Closing band */}
      <section className="relative overflow-hidden py-28 md:py-44 border-t border-white/10" data-testid="home-closing">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <img src={c.hero?.background_image} alt="" className="w-full h-full object-cover opacity-[0.08] grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/85 to-obsidian" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 30%, rgba(79,176,74,0.14), transparent 60%)" }} />
        </div>

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="overline text-gold mb-8">Still Building</p>
            <blockquote className="font-editorial italic text-3xl md:text-6xl leading-[1.05] text-white">
              "I help through my music. Knowing I can put something out and people feel it, that's the biggest accolade for me."
            </blockquote>
            <p className="mt-8 font-display uppercase tracking-[0.28em] text-xs text-white/50">Timbaland</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/discography"
                data-testid="closing-cta-discography"
                className="group inline-flex items-center gap-3 bg-gold text-black font-display uppercase tracking-[0.2em] text-sm px-9 py-4 hover:bg-white transition-colors duration-300 rounded-full"
              >
                Explore The Catalog
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link
                to="/biography"
                data-testid="closing-cta-origin"
                className="group inline-flex items-center gap-3 border border-white/25 text-white font-display uppercase tracking-[0.2em] text-sm px-9 py-4 hover:bg-white/10 transition-colors duration-300 rounded-full"
              >
                Read The Origin Story
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
